/**
 * The tutor half of the window. Nothing clever here on purpose — the intelligence
 * is all server-side. This streams, keeps the transcript, and ends the session.
 */

import type React from "react";

import CodeMirror from "@uiw/react-codemirror";
import { javascript } from "@codemirror/lang-javascript";
import { python } from "@codemirror/lang-python";
import { useEffect, useMemo, useRef, useState } from "react";

import { parseMessage } from "../tutorText";

import type { EndSessionResult } from "../api";

export interface ChatMessage {
  role: "learner" | "tutor" | "system";
  text: string;
  runCount?: number;
}

interface Props {
  displayName: string;
  messages: ChatMessage[];
  streaming: string | null;
  draft: string;
  onDraftChange: (text: string) => void;
  onSend: () => void;
  busy: boolean;
  bufferedCount: number;
  onEnd: () => void;
  ending: boolean;
  outcome: EndSessionResult | null;
  /** The domain's language, used when the tutor omits it from a code fence. */
  language: string;
}

export function TutorPanel({
  displayName,
  messages,
  streaming,
  draft,
  onDraftChange,
  onSend,
  busy,
  bufferedCount,
  onEnd,
  ending,
  outcome,
  language,
}: Props) {
  const bottom = useRef<HTMLDivElement>(null);
  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, streaming]);

  const canSend = !busy && (draft.trim().length > 0 || bufferedCount > 0);

  return (
    <section className="panel tutor" aria-label="Tutor">
      <header className="panel-head">
        <div className="panel-title">Tutor</div>
        <button type="button" onClick={onEnd} disabled={ending || !!outcome} className="ghost">
          {ending ? "Saving…" : outcome ? "Session saved" : "End session"}
        </button>
      </header>

      <div className="transcript">
        {messages.map((message, index) => (
          <div key={index} className={`bubble ${message.role}`}>
            <div className="who">{message.role === "learner" ? displayName : message.role === "tutor" ? "Tutor" : ""}</div>
            <div className="text">
              <TutorText text={message.text} language={language} />
            </div>
            {!!message.runCount && (
              <div className="attached">
                + {message.runCount} run{message.runCount === 1 ? "" : "s"} attached
              </div>
            )}
          </div>
        ))}
        {streaming !== null && (
          <div className="bubble tutor">
            <div className="who">Tutor</div>
            <div className="text">
              <TutorText text={streaming} language={language} />
              <span className="caret" />
            </div>
          </div>
        )}
        {outcome && <SessionOutcome outcome={outcome} />}
        <div ref={bottom} />
      </div>

      <form
        className="composer"
        onSubmit={(event) => {
          event.preventDefault();
          if (canSend) onSend();
        }}
      >
        <textarea
          value={draft}
          onChange={(event) => onDraftChange(event.target.value)}
          onKeyDown={(event) => {
            // Enter sends, shift+Enter makes a new line — what a teenager expects
            // from every chat app they have ever used.
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              if (canSend) onSend();
            }
          }}
          rows={2}
          placeholder={bufferedCount > 0 ? "Ask about what just happened…" : "Say something…"}
          disabled={busy || !!outcome}
        />
        <button type="submit" disabled={!canSend} className="primary">
          Send
        </button>
      </form>
    </section>
  );
}

/**
 * What the learner sees when the session is saved.
 *
 * Deliberately almost nothing. This card used to print the update pass's own report —
 * concept ids, `0.00 → 0.60`, the evidence lines, the validator's review flags — which
 * is the facilitator's view leaking onto the learner's screen. It is wrong there twice
 * over: mastery numbers are shown to an adult and nowhere else (see the skill map, which
 * says "can do it with hints" rather than a score), and a wall of machine output is a
 * poor last thing to read at the end of a session. The facilitator dashboard still has
 * every delta, warning and error, which is where they were always meant to be read.
 *
 * A failure still says so, because the learner is the one who can tell a facilitator —
 * but it says it in a sentence, without the codes.
 */
export function SessionOutcome({ outcome }: { outcome: EndSessionResult }) {
  const failed = !outcome.applied || outcome.errors.length > 0;
  return (
    <div className={`outcome-card ${failed ? "rejected" : "applied"}`}>
      {failed ? (
        <>
          <strong>Your work is saved, but your skill map did not update.</strong>
          <div className="muted">Let a facilitator know — nothing you did today is lost.</div>
        </>
      ) : (
        <>
          <strong>Session saved.</strong>
          <div className="muted">
            Your skill map has what you did today. See you next time.
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Render a tutor message with its code shown as code.
 *
 * Prose is emitted verbatim; only the code is treated specially. See tutorText.ts
 * for why this is not a Markdown renderer.
 */
function TutorText({ text, language }: { text: string; language: string }) {
  const segments = parseMessage(text);
  return (
    <>
      {segments.map((segment, index) => {
        // Blocks first, so the rest is narrowed to the kinds that can carry emphasis.
        if (segment.kind === "block") {
          return (
            <CodeBlock
              key={index}
              code={segment.text}
              language={segment.language}
              fallbackLanguage={language}
            />
          );
        }

        // Emphasis travels with whatever it wrapped, because the tutor writes things
        // like "**Write me a `while` loop**" where the snippet is part of the sentence.
        const wrap = (node: React.ReactNode) => {
          let out = node;
          if (segment.em) out = <em>{out}</em>;
          if (segment.strong) out = <strong>{out}</strong>;
          return out;
        };

        if (segment.kind === "text") return <span key={index}>{wrap(segment.text)}</span>;
        return (
          <span key={index}>{wrap(<code className="inline-code">{segment.text}</code>)}</span>
        );
      })}
    </>
  );
}

/**
 * Highlighted, read-only code inside a tutor message.
 *
 * The same CodeMirror and the same language modes the editor uses, so a snippet in the
 * tutor's message is coloured **identically** to that snippet once the learner types it
 * in. That equivalence is the point: a beginner comparing one against the other should
 * not have to work out whether two differently-coloured things are the same code.
 *
 * Read-only and chrome-free — no line numbers, no gutter, no cursor — so it reads as a
 * quotation rather than a second editor they might try to run.
 */
function HighlightedCode({ code, language }: { code: string; language: string }) {
  const extensions = useMemo(
    () => [language === "python" ? python() : javascript()],
    [language],
  );
  return (
    <CodeMirror
      value={code}
      theme="dark"
      extensions={extensions}
      editable={false}
      basicSetup={false}
      className="quoted-code"
    />
  );
}

function CodeBlock({
  code,
  language,
  fallbackLanguage,
}: {
  code: string;
  language: string;
  fallbackLanguage: string;
}) {
  const [copied, setCopied] = useState(false);
  // The tutor often omits the language on a fence — every Strudel snippet arrives as a
  // bare ```. Labelling that "code" is accurate and useless; the session already knows
  // which language this domain is in.
  const shown = language || fallbackLanguage;

  return (
    <div className="code-block">
      <div className="code-head">
        <span className="lang">{shown}</span>
        <button
          type="button"
          className="copy"
          onClick={() => {
            // Copies the code alone — no fence, no language line. Typing it out is
            // often better practice, but a learner who copies should not be punished
            // for it with a syntax error they did not write.
            void navigator.clipboard?.writeText(code).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            });
          }}
        >
          {copied ? "copied" : "copy"}
        </button>
      </div>
      <HighlightedCode code={code} language={shown} />
    </div>
  );
}
